import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaHome, FaArrowLeft, FaSearch, FaTimes } from "react-icons/fa";
import BanquetCard from "../components/banquet/BanquetCard.jsx";
import BanquetFilters from "../components/banquet/BanquetFilters.jsx";
import BanquetHeroSlider from "../components/banquet/BanquetHeroSlider.jsx";
import ListingMediaPreview from "../components/ListingMediaPreview.jsx";
import { banquetCategories } from "../data/banquetsData.js";
import { getPublishedBanquets } from "../services/publicBanquetService.js";
import { buildBanquetResults, defaultBanquetFilters } from "../utils/banquetFilters.js";

const decorPreviews = [
  {
    title: "First Birthday Decor",
    to: "/subcategory/first-birthday-decorations",
    video: "/videos/gallery/look2-first-birthday-stage.mp4",
    poster: "/videos/gallery/look2-first-birthday-stage-thumb.jpg",
  },
  {
    title: "Baby Shower Decor",
    to: "/subcategory/baby-shower-decoration",
    video: "/videos/gallery/look3-baby-shower-theme.mp4",
    poster: "/videos/gallery/look3-baby-shower-theme-thumb.jpg",
  },
  {
    title: "Welcome Baby Decor",
    to: "/subcategory/welcome-baby-decoration",
    poster: "/images/home-preview-v2/welcome-baby.webp",
  },
  {
    title: "Birthday Decoration",
    to: "/subcategory/birthday-decoration",
    poster: "/images/home-preview-v2/birthday-decoration.webp",
  },
  {
    title: "Wedding Events",
    to: "/subcategory/wedding-events",
    poster: "/images/home-preview-v2/wedding-events.webp",
  },
  {
    title: "Anniversary Decoration",
    to: "/subcategory/anniversary-decoration",
    poster: "/images/Anniversary-Decoration.webp",
  },
];

const selectClassName =
  "min-h-[46px] w-full rounded-2xl border border-emerald-100 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-800 shadow-sm outline-none transition hover:border-emerald-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100";

const sortLabels = {
  "": "Recommended",
  nearest: "Nearest",
  "lowest-price": "Lowest Price",
  "highest-capacity": "Highest Capacity",
  "best-rated": "Best Rated",
};

const filterLabels = {
  name: (value) => `Search: ${value}`,
  area: (value) => `Area: ${value}`,
  minimumGuestRange: (value) => `Minimum Guests: ${value}`,
  pincode: (value) => `Pincode: ${value}`,
  distanceRadius: (value, filters) => (filters.pincode ? `Within ${value} km` : ""),
  foodType: (value) =>
    ({
      "veg-only": "Veg Only",
      "non-veg": "Non-Veg Available",
      "veg-non-veg": "Veg + Non-Veg",
    }[value] || ""),
  alcohol: (value) => (value === "allowed" ? "Alcohol Allowed" : value === "not-allowed" ? "Alcohol Not Allowed" : ""),
  parkingStatus: (value) => (value === "yes" ? "Parking Available" : value === "no" ? "No Parking" : ""),
  parkingCapacity: (value) => `${value}+ Parking`,
  valetParking: () => "Valet Parking",
  capacityTier: (value) => `Capacity ${value}`,
  priceTier: (value) =>
    ({
      "under-1000": "Under Rs.1000 / plate",
      "1000-1500": "Rs.1000 - Rs.1500 / plate",
      "1500-2000": "Rs.1500 - Rs.2000 / plate",
      "2000-plus": "Rs.2000+",
      "hall-rental": "Hall Rental Available",
    }[value] || ""),
  availability: (value) =>
    ({
      today: "Available Today",
      weekend: "Available This Weekend",
      lunch: "Lunch Available",
      dinner: "Dinner Available",
    }[value] || ""),
  eventDate: (value, filters) => {
    const label = new Date(`${value}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    const slotLabel = { lunch: "Lunch", dinner: "Dinner", fullday: "Full Day" }[filters.eventSlot] || "Full Day";
    return `${label} (${slotLabel})`;
  },
};

export default function BanquetsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(defaultBanquetFilters);
  const [activeQuickChips, setActiveQuickChips] = useState([]);
  const [hasUsedNearMe, setHasUsedNearMe] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [nearMeStatus, setNearMeStatus] = useState("idle");
  const [nearMeMessage, setNearMeMessage] = useState("");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [publicBanquets, setPublicBanquets] = useState([]);
  const [loadState, setLoadState] = useState({ isLoading: true, error: "" });
  const listingsRef = useRef(null);
  const banquetTestModeEnabled = false;

  useEffect(() => {
    let isMounted = true;
    const loadBanquets = async () => {
      try {
        const banquets = await getPublishedBanquets();
        if (!isMounted) return;
        setPublicBanquets(banquets);
        setLoadState({ isLoading: false, error: "" });
      } catch (error) {
        if (!isMounted) return;
        setPublicBanquets([]);
        setLoadState({
          isLoading: false,
          error: "Banquet listings are being verified. Please contact DecorZoom on WhatsApp.",
        });
      }
    };

    loadBanquets();

    return () => {
      isMounted = false;
    };
  }, []);

  const cities = useMemo(
    () => [...new Set(publicBanquets.map((banquet) => banquet.city).filter(Boolean))].sort(),
    [publicBanquets]
  );
  const localities = useMemo(
    () => [...new Set(publicBanquets.map((banquet) => banquet.locality).filter(Boolean))].sort(),
    [publicBanquets]
  );

  const { results: filteredBanquets, resolvedPincode, areaSearch, nearbyFallback, mixedNearbyFallback } = useMemo(
    () => buildBanquetResults(publicBanquets, filters, activeQuickChips, userLocation),
    [activeQuickChips, filters, publicBanquets, userLocation]
  );

  const updateFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  const toggleQuickChip = (chip) =>
    setActiveQuickChips((current) =>
      current.includes(chip) ? current.filter((item) => item !== chip) : [...current, chip]
    );

  const nearMeActive = Boolean(userLocation);
  const shouldPulseNearMe = !nearMeActive && !hasUsedNearMe;

  const resetNearMe = () => {
    setUserLocation(null);
    setNearMeStatus("idle");
    setNearMeMessage("");
  };

  const handleNearMeClick = () => {
    setHasUsedNearMe(true);

    if (userLocation) {
      resetNearMe();
      return;
    }

    if (!navigator.geolocation) {
      setNearMeStatus("error");
      setNearMeMessage("Location isn't supported on this device — showing all banquets.");
      return;
    }

    setNearMeStatus("requesting");
    setNearMeMessage("");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude });
        setNearMeStatus("active");
        setNearMeMessage("");
        scrollToListings();
      },
      (error) => {
        setNearMeStatus(error.code === error.PERMISSION_DENIED ? "denied" : "error");
        setNearMeMessage(
          error.code === error.PERMISSION_DENIED
            ? "Location access denied — showing all banquets."
            : "Couldn't detect your location — showing all banquets."
        );
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  const resetFilters = () => {
    setFilters(defaultBanquetFilters);
    setActiveQuickChips([]);
    resetNearMe();
  };

  const removeFilter = (key, value) => {
    if (key === "quickChip") {
      toggleQuickChip(value);
      return;
    }

    if (key === "venueTypes" || key === "eventTypes") {
      setFilters((current) => ({ ...current, [key]: current[key].filter((item) => item !== value) }));
      return;
    }

    if (key === "eventDate") {
      setFilters((current) => ({ ...current, eventDate: "", eventSlot: defaultBanquetFilters.eventSlot }));
      return;
    }

    if (typeof defaultBanquetFilters[key] === "boolean") {
      updateFilter(key, false);
      return;
    }

    updateFilter(key, defaultBanquetFilters[key] ?? "");
  };

  const selectedFilters = useMemo(() => {
    const chips = [];
    Object.entries(filterLabels).forEach(([key, labelBuilder]) => {
      const value = filters[key];
      if (typeof value === "boolean" && value) {
        chips.push({ key, label: labelBuilder(value, filters) });
      } else if (value && !Array.isArray(value)) {
        const label = labelBuilder(value, filters);
        if (label) chips.push({ key, label });
      }
    });

    (filters.venueTypes || []).forEach((value) => chips.push({ key: "venueTypes", value, label: value }));
    (filters.eventTypes || []).forEach((value) => chips.push({ key: "eventTypes", value, label: value }));
    activeQuickChips.forEach((value) => chips.push({ key: "quickChip", value, label: value }));
    return chips;
  }, [activeQuickChips, filters]);

  const scrollToListings = () => {
    listingsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const resultHeading = areaSearch
    ? nearbyFallback
      ? "Nearby venues you may also like"
      : `Venues in ${areaSearch}`
    : "Banquet Listings";
  const resultSubcopy = nearbyFallback
    ? "Popular venues around your searched area"
    : mixedNearbyFallback
      ? "Exact matches are shown first, followed by nearby venues you may also like."
      : "Verified banquet options for birthdays, family functions and celebrations.";

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f5f7f4] pb-12">
      <title>Banquet Halls &amp; Venues in Delhi NCR | Book Online | DecorZoom</title>
      <meta
        name="description"
        content="Browse verified banquet halls, farmhouses and lawns across Delhi NCR. Compare pricing, capacity, amenities and check live availability instantly on DecorZoom."
      />
      <link rel="canonical" href="https://www.decorzoom.in/banquets" />
      <meta property="og:title" content="Banquet Halls & Venues in Delhi NCR | DecorZoom" />
      <meta
        property="og:description"
        content="Browse verified banquet halls, farmhouses and lawns across Delhi NCR with live pricing and availability."
      />
      <meta property="og:url" content="https://www.decorzoom.in/banquets" />
      <meta property="og:type" content="website" />

      <BanquetHeroSlider onBannerClick={scrollToListings} />

      <section className="mx-auto mt-4 max-w-7xl px-3 sm:mt-5 sm:px-4">
        <div className="relative rounded-[26px] border border-emerald-100/80 bg-white p-3 shadow-[0_24px_60px_-38px_rgba(15,23,42,0.55)] ring-1 ring-amber-100/80 sm:rounded-[32px] sm:p-4">
          <FaSearch className="pointer-events-none absolute left-6 top-1/2 -translate-y-1/2 text-xl text-emerald-600 sm:left-8 sm:text-2xl" />
          <input
            type="text"
            value={filters.name}
            onChange={(event) => updateFilter("name", event.target.value)}
            placeholder="Search banquet by name, e.g. Rosellia Banquets &amp; Suites"
            className="w-full min-h-[56px] rounded-[20px] border-2 border-emerald-100 bg-emerald-50/40 py-3 pl-14 pr-12 text-base font-semibold text-slate-900 shadow-inner outline-none transition placeholder:font-normal placeholder:text-slate-400 hover:border-emerald-200 focus:border-emerald-500 focus:bg-white focus:ring-4 focus:ring-emerald-100 sm:min-h-[64px] sm:pl-16 sm:pr-14 sm:text-lg"
          />
          {filters.name ? (
            <button
              type="button"
              onClick={() => updateFilter("name", "")}
              aria-label="Clear search"
              className="absolute right-6 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 sm:right-8"
            >
              <FaTimes className="text-lg" />
            </button>
          ) : null}
        </div>
      </section>

      <section className="mx-auto mt-3 max-w-7xl px-3 sm:mt-4 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleNearMeClick}
            disabled={nearMeStatus === "requesting"}
            aria-pressed={nearMeActive}
            className={`inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full border-2 px-5 py-3 text-sm font-black transition disabled:cursor-wait disabled:opacity-80 sm:w-auto sm:text-base ${
              nearMeActive
                ? "border-emerald-700 bg-emerald-700 text-white shadow-[0_14px_30px_-16px_rgba(4,120,87,0.85)]"
                : "border-emerald-200 bg-white text-emerald-800 hover:border-emerald-400 hover:bg-emerald-50"
            } ${shouldPulseNearMe ? "motion-safe:animate-near-me-pulse" : ""}`}
          >
            <span aria-hidden="true">📍</span>{" "}
            {nearMeStatus === "requesting" ? "Detecting location..." : nearMeActive ? "Near Me: On" : "Banquet Near Me"}
          </button>
          {nearMeActive ? (
            <button
              type="button"
              onClick={resetNearMe}
              aria-label="Clear Near Me distance sorting"
              className="inline-flex min-h-[36px] min-w-[36px] items-center justify-center rounded-full border border-emerald-200 bg-white text-emerald-800 shadow-sm transition hover:border-emerald-400 hover:bg-emerald-50"
            >
              <FaTimes />
            </button>
          ) : null}
        </div>
        {nearMeMessage ? (
          <p className="mt-2 text-sm font-medium text-amber-800" role="status">
            {nearMeMessage}
          </p>
        ) : null}
      </section>

      <section className="mx-auto mt-4 max-w-7xl px-3 sm:mt-5 sm:px-4">
        <div className="grid gap-4 xl:grid-cols-[320px_minmax(0,1fr)] xl:items-start">
          <aside className="xl:sticky xl:top-4 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => navigate("/")}
                className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-2xl border border-emerald-100 bg-white px-3 py-2.5 text-sm font-bold text-emerald-800 shadow-sm transition hover:border-amber-200 hover:bg-amber-50"
              >
                <FaHome /> Home
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex min-h-[46px] items-center justify-center gap-2 rounded-2xl border border-emerald-100 bg-white px-3 py-2.5 text-sm font-bold text-emerald-800 shadow-sm transition hover:border-amber-200 hover:bg-amber-50"
              >
                <FaArrowLeft /> Back
              </button>
            </div>

            <BanquetFilters
              filters={filters}
              cities={cities}
              localities={localities}
              quickChips={banquetCategories}
              activeQuickChips={activeQuickChips}
              onFilterChange={updateFilter}
              onToggleQuickChip={toggleQuickChip}
              onReset={resetFilters}
              mobileOpen={mobileFilterOpen}
              setMobileOpen={setMobileFilterOpen}
              resolvedPincode={resolvedPincode}
            />

            <div className="hidden rounded-[24px] border border-emerald-100 bg-white p-4 shadow-[0_14px_35px_-28px_rgba(15,23,42,0.35)] ring-1 ring-amber-100/70 xl:block">
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">Explore Decor Options</p>
              <h3 className="mt-1 text-base font-bold text-slate-950">DecorZoom se decoration bhi book karein</h3>

              <div className="mt-3 max-h-[560px] space-y-3 overflow-y-auto pr-1">
                {decorPreviews.map((item) => (
                  <div
                    key={item.to}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(item.to)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        navigate(item.to);
                      }
                    }}
                    className="group relative h-[180px] w-full shrink-0 cursor-pointer overflow-hidden rounded-2xl border border-emerald-100 shadow-sm transition hover:shadow-md"
                  >
                    <ListingMediaPreview
                      videoSrc={item.video}
                      posterSrc={item.poster}
                      alt={item.title}
                      className="h-full w-full [&_img]:scale-100 [&_img]:transition-transform [&_img]:duration-700 [&_img]:ease-out group-hover:[&_img]:scale-110 [&_video]:scale-100 [&_video]:transition-transform [&_video]:duration-700 [&_video]:ease-out group-hover:[&_video]:scale-110"
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(15,23,42,0)_45%,rgba(6,78,59,0.88)_100%)] opacity-90 transition-opacity duration-300 group-hover:opacity-100" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h4 className="text-lg font-bold text-white">{item.title}</h4>
                      <p className="mt-1 text-xs font-semibold text-amber-200">Tap to explore decoration themes</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </aside>

          <div className="min-w-0 space-y-4">
            <div
              ref={listingsRef}
              className="min-w-0 rounded-[24px] border border-emerald-100/80 bg-white px-4 py-4 shadow-[0_18px_45px_-32px_rgba(15,23,42,0.45)] ring-1 ring-amber-100/70 sm:rounded-[28px] sm:px-5"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h1 className="text-xl font-bold text-slate-950 sm:text-2xl">{resultHeading}</h1>
                  <p className="mt-1 text-sm text-slate-600">
                    {loadState.isLoading ? "Loading verified banquet listings" : `${filteredBanquets.length} result${filteredBanquets.length === 1 ? "" : "s"}`}
                    {resolvedPincode ? ` near ${filters.pincode}` : ""} for banquet discovery
                  </p>
                  <p className="mt-1 text-sm font-medium text-emerald-700">{resultSubcopy}</p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="hidden flex-wrap gap-2 sm:flex">
                    {resolvedPincode ? (
                      <span className="min-h-[36px] rounded-full bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700">
                        Distance visible
                      </span>
                    ) : null}
                    {activeQuickChips.map((chip) => (
                      <button
                        key={chip}
                        type="button"
                        onClick={() => toggleQuickChip(chip)}
                        className="min-h-[36px] rounded-full bg-gradient-to-r from-emerald-700 to-emerald-600 px-3 py-2 text-xs font-bold text-white shadow-sm ring-2 ring-amber-200/70"
                      >
                        {chip} x
                      </button>
                    ))}
                  </div>

                  <div className="w-full sm:min-w-[220px]">
                    <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">Sort By</label>
                    <select
                      value={filters.sortBy}
                      onChange={(event) => updateFilter("sortBy", event.target.value)}
                      className={selectClassName}
                    >
                      <option value="">Recommended</option>
                      <option value="nearest">{sortLabels.nearest}</option>
                      <option value="lowest-price">{sortLabels["lowest-price"]}</option>
                      <option value="highest-capacity">{sortLabels["highest-capacity"]}</option>
                      <option value="best-rated">{sortLabels["best-rated"]}</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {selectedFilters.length ? (
              <div className="rounded-[24px] border border-emerald-100/80 bg-gradient-to-r from-white via-emerald-50/50 to-amber-50/60 px-4 py-3 shadow-[0_16px_40px_-32px_rgba(15,23,42,0.45)] ring-1 ring-white">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-800">Selected Filters</span>
                  {selectedFilters.map((chip) => (
                    <button
                      key={`${chip.key}-${chip.value || chip.label}`}
                      type="button"
                      onClick={() => removeFilter(chip.key, chip.value)}
                      className="min-h-[36px] rounded-full bg-gradient-to-r from-emerald-700 to-emerald-600 px-3 py-1.5 text-xs font-bold text-white shadow-[0_10px_20px_-14px_rgba(4,120,87,0.85)] ring-2 ring-amber-200/80"
                    >
                      {chip.label} x
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="min-h-[36px] rounded-full border border-emerald-200 bg-white px-3 py-1.5 text-xs font-bold text-emerald-800 shadow-sm"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            ) : null}

            <div className="space-y-4">
              {!loadState.isLoading && filteredBanquets.map((banquet) => (
                <BanquetCard
                  key={banquet.id}
                  banquet={banquet}
                  showLiveDistanceBadge={nearMeActive}
                  searchContext={{
                    ...(resolvedPincode ? { pincode: filters.pincode, radius: filters.distanceRadius } : {}),
                    banquetTestMode: banquetTestModeEnabled,
                  }}
                />
              ))}
            </div>

            {loadState.isLoading ? (
              <div className="rounded-[24px] border border-slate-200 bg-white p-10 text-center shadow-sm">
                <h3 className="text-2xl font-semibold text-slate-900">Loading verified banquet listings</h3>
                <p className="mt-3 text-sm text-slate-600">Please wait while we fetch live banquet records.</p>
              </div>
            ) : filteredBanquets.length === 0 ? (
              <div className="rounded-[24px] border border-dashed border-slate-300 bg-white p-10 text-center shadow-sm">
                <h3 className="text-2xl font-semibold text-slate-900">No exact venues found</h3>
                <p className="mt-3 text-sm text-slate-600">
                  {loadState.error || "Try nearby areas or reduce filters"}
                </p>
                <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
                  <button type="button" onClick={resetFilters} className="inline-flex min-h-[44px] items-center justify-center rounded-full border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-800">
                    Clear Filters
                  </button>
                  <a href="https://wa.me/918377982191" className="inline-flex min-h-[44px] items-center justify-center rounded-full bg-emerald-600 px-5 py-3 text-sm font-semibold text-white">
                    Contact DecorZoom
                  </a>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
